import { FREE_TIER_DAILY_NEW_GRAPHEME_LIMIT } from "@-/dev.improve.haniverse.lib.entitlement.free-tier";

/** The fixed limits Settings → Daily New Cards offers beside Custom and No Limit. */
export const DAILY_NEW_GRAPHEME_LIMIT_PRESETS = [10, 20] as const;

/** Bounds the Custom field to three digits; a larger number is No Limit in practice. */
export const MAX_CUSTOM_DAILY_NEW_GRAPHEME_LIMIT = 999;

export type ResolvedDailyNewGraphemeLimit = {
  /**
   * The free tier's cap is what binds, not a lower limit the learner chose, so only Premium lifts
   * it. Never true for premium.
   */
  isFreeTierCap: boolean;
  /** New graphemes allowed per local day; `Infinity` when nothing caps the day. */
  limit: number;
};

/**
 * The day's effective new-grapheme limit: the learner's own setting (`user.dailyNewGraphemeLimit`,
 * `null` for No Limit), held to {@link FREE_TIER_DAILY_NEW_GRAPHEME_LIMIT} for a free learner. A
 * free learner who never chose a limit, or chose one the free tier does not allow (e.g. while
 * premium), therefore gets the free tier's cap.
 */
export function resolveDailyNewGraphemeLimit({
  dailyNewGraphemeLimit,
  isPremium,
}: {
  dailyNewGraphemeLimit: number | null;
  isPremium: boolean;
}): ResolvedDailyNewGraphemeLimit {
  const chosenLimit = dailyNewGraphemeLimit ?? Number.POSITIVE_INFINITY;
  if (isPremium || chosenLimit < FREE_TIER_DAILY_NEW_GRAPHEME_LIMIT) {
    return { isFreeTierCap: false, limit: chosenLimit };
  }
  return { isFreeTierCap: true, limit: FREE_TIER_DAILY_NEW_GRAPHEME_LIMIT };
}

/**
 * How many more new graphemes may be introduced today under `limit`. `Infinity` without a limit;
 * never negative for a learner already past it (e.g. after lowering the limit mid-day).
 */
export function remainingDailyNewGraphemes({
  introducedTodayCount,
  limit,
}: {
  introducedTodayCount: number;
  limit: number;
}): number {
  return Math.max(0, limit - introducedTodayCount);
}

/** How Settings shows a resolved limit: the number, or "No Limit". */
export function dailyNewGraphemeLimitLabel(limit: number): string {
  return Number.isFinite(limit) ? limit.toLocaleString() : "No Limit";
}

/** The largest Custom limit the learner's tier may save. */
export function maxCustomDailyNewGraphemeLimit({ isPremium }: { isPremium: boolean }): number {
  return isPremium ? MAX_CUSTOM_DAILY_NEW_GRAPHEME_LIMIT : FREE_TIER_DAILY_NEW_GRAPHEME_LIMIT;
}
